import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  TextField,
  Button,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  Add as AddIcon
} from '@mui/icons-material';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import api from '../../services/api';
import { THROTTLE_CONFIGS } from '../../config/Config';

const ThrottleConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newConfig, setNewConfig] = useState({
    key: '',
    max_attempts: 5,
    time_window_seconds: 3600,
    description: '',
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchThrottleConfigs();
  }, []);

  const fetchThrottleConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get(THROTTLE_CONFIGS);
      // Ensure configs is always an array
      const configsData = Array.isArray(response.data) ? response.data : 
                         (response.data?.results || response.data || []);
      setConfigs(configsData);
    } catch (error) {
      console.error('Error fetching throttle configs:', error);
      setConfigs([]); // Set empty array on error
      setSnackbar({
        open: true,
        message: 'Failed to load throttle configurations',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditConfig = (config) => {
    setEditingConfig({ ...config });
    setEditDialogOpen(true);
  };

  const handleAddConfig = () => {
    setNewConfig({
      key: '',
      max_attempts: 5,
      time_window_seconds: 3600,
      description: '',
      is_active: true
    });
    setAddDialogOpen(true);
  };

  const handleSaveConfig = async () => {
    if (!editingConfig) return;

    try {
      setSaving(true);
      const response = await api.put(`${THROTTLE_CONFIGS}${editingConfig.id}/`, editingConfig);
      
      setConfigs(configs.map(config => 
        config.id === editingConfig.id ? response.data : config
      ));
      
      setEditDialogOpen(false);
      setEditingConfig(null);
      setSnackbar({
        open: true,
        message: 'Throttle configuration updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating throttle config:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update throttle configuration',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNewConfig = async () => {
    if (!newConfig.key.trim()) {
      setSnackbar({
        open: true,
        message: 'Key is required',
        severity: 'error'
      });
      return;
    }

    try {
      setSaving(true);
      const response = await api.post(THROTTLE_CONFIGS, newConfig);
      
      setConfigs([...configs, response.data]);
      setAddDialogOpen(false);
      setNewConfig({
        key: '',
        max_attempts: 5,
        time_window_seconds: 3600,
        description: '',
        is_active: true
      });
      setSnackbar({
        open: true,
        message: 'Throttle configuration created successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating throttle config:', error);
      setSnackbar({
        open: true,
        message: 'Failed to create throttle configuration',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingConfig(null);
  };

  const handleCancelAdd = () => {
    setAddDialogOpen(false);
    setNewConfig({
      key: '',
      max_attempts: 5,
      time_window_seconds: 3600,
      description: '',
      is_active: true
    });
  };

  const handleToggleActive = async (config) => {
    try {
      const updatedConfig = { ...config, is_active: !config.is_active };
      const response = await api.put(`${THROTTLE_CONFIGS}${config.id}/`, updatedConfig);
      
      setConfigs(configs.map(c => 
        c.id === config.id ? response.data : c
      ));
      
      setSnackbar({
        open: true,
        message: `Configuration ${updatedConfig.is_active ? 'activated' : 'deactivated'} successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling config:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update configuration status',
        severity: 'error'
      });
    }
  };

  const formatTimeWindow = (seconds) => {
    if (seconds < 60) return `${seconds} seconds`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours`;
    return `${Math.floor(seconds / 86400)} days`;
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'default';
  };

  if (loading) {
    return (
      <AdminLayoutWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayoutWrapper>
    );
  }

  return (
    <AdminLayoutWrapper>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SpeedIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Throttle Configuration
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddConfig}
              sx={{ mr: 1 }}
            >
              Add Rule
            </Button>
            <Tooltip title="Refresh configurations">
              <IconButton 
                onClick={fetchThrottleConfigs} 
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Rate Limiting Rules
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure rate limiting rules for different actions in the system. These rules help prevent abuse and ensure fair usage.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Action</strong></TableCell>
                    <TableCell><strong>Max Attempts</strong></TableCell>
                    <TableCell><strong>Time Window</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(configs) && configs.length > 0 ? (
                    configs.map((config) => (
                      <TableRow key={config.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {config.key}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {config.max_attempts} attempts
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTimeWindow(config.time_window_seconds)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={config.is_active ? 'Active' : 'Inactive'}
                            color={getStatusColor(config.is_active)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {config.description || 'No description'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit configuration">
                              <IconButton
                                size="small"
                                onClick={() => handleEditConfig(config)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={config.is_active ? 'Deactivate' : 'Activate'}>
                              <Switch
                                checked={config.is_active}
                                onChange={() => handleToggleActive(config)}
                                size="small"
                              />
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No throttle configurations found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Throttle Configuration</DialogTitle>
          <DialogContent>
            {editingConfig && (
              <Box sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  label="Key"
                  value={editingConfig.key}
                  disabled
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Max Attempts"
                  type="number"
                  value={editingConfig.max_attempts}
                  onChange={(e) => setEditingConfig({
                    ...editingConfig,
                    max_attempts: parseInt(e.target.value)
                  })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Time Window (seconds)"
                  type="number"
                  value={editingConfig.time_window_seconds}
                  onChange={(e) => setEditingConfig({
                    ...editingConfig,
                    time_window_seconds: parseInt(e.target.value)
                  })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={editingConfig.description || ''}
                  onChange={(e) => setEditingConfig({
                    ...editingConfig,
                    description: e.target.value
                  })}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEdit} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveConfig}
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Dialog */}
        <Dialog open={addDialogOpen} onClose={handleCancelAdd} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Throttle Configuration</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Key"
                value={newConfig.key}
                onChange={(e) => setNewConfig({
                  ...newConfig,
                  key: e.target.value
                })}
                placeholder="e.g., api_rate_limit, file_upload"
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Max Attempts"
                type="number"
                value={newConfig.max_attempts}
                onChange={(e) => setNewConfig({
                  ...newConfig,
                  max_attempts: parseInt(e.target.value) || 5
                })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Time Window (seconds)"
                type="number"
                value={newConfig.time_window_seconds}
                onChange={(e) => setNewConfig({
                  ...newConfig,
                  time_window_seconds: parseInt(e.target.value) || 3600
                })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newConfig.description}
                onChange={(e) => setNewConfig({
                  ...newConfig,
                  description: e.target.value
                })}
                placeholder="Describe what this throttle configuration is for"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelAdd} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveNewConfig}
              variant="contained"
              disabled={saving || !newConfig.key.trim()}
              startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
            >
              Add Configuration
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayoutWrapper>
  );
};

export default ThrottleConfigPage;
